import {
  ArrowBack as ArrowBackIcon,
  MarkEmailRead as MarkEmailReadIcon,
} from '../../components/icons';
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  TextField,
  Button,
  Link,
  Alert,
  CircularProgress,
  Avatar,
  Card,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useAuth } from '../../contexts/AuthContext';
import { getErrorMessage } from '../../utils/errorUtils';
import { BRAND, SUCCESS, NEUTRAL } from '../../theme/adminColors';

const RESEND_COOLDOWN_SECONDS = 60;

const CodeTextField = styled(TextField)(() => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: '12px',
    backgroundColor: '#f8fafc',
    '& fieldset': {
      border: '2px solid #e2e8f0',
    },
    '&.Mui-focused fieldset': {
      borderColor: BRAND.main,
      borderWidth: '2px',
    },
  },
  '& input': {
    textAlign: 'center',
    fontSize: '1.75rem',
    fontWeight: 700,
    letterSpacing: '0.6rem',
    color: NEUTRAL.text,
  },
}));

export default function EmailVerificationPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { verifyEmail, resendVerificationCode, logout } = useAuth();

  const email = location.state?.email || '';

  const [code, setCode] = useState('');
  const [error, setError] = useState(null);
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [cooldown, setCooldown] = useState(0);

  useEffect(() => {
    if (!email) {
      navigate('/register', { replace: true });
    }
  }, [email, navigate]);

  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const timer = setInterval(() => {
      setCooldown((prev) => (prev <= 1 ? 0 : prev - 1));
    }, 1000);
    return () => clearInterval(timer);
  }, [cooldown]);

  const handleCodeChange = useCallback((event) => {
    const digitsOnly = event.target.value.replace(/\D/g, '').slice(0, 6);
    setCode(digitsOnly);
  }, []);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (code.length !== 6) {
      setError('Please enter the 6-digit code from your email.');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setInfo(null);
      await verifyEmail(email, code);
      navigate('/setup-merchant', { replace: true });
    } catch (err) {
      const errorMessage = err.userMessage || getErrorMessage(err, 'Verification failed. Please try again.');
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleBackToLogin = async () => {
    // The user is signed in but unverified at this point, so clear the session
    // before sending them to the login screen.
    await logout();
    navigate('/login', { replace: true });
  };

  const handleResend = async () => {
    if (cooldown > 0) return;
    try {
      setResending(true);
      setError(null);
      setInfo(null);
      await resendVerificationCode(email);
      setInfo('A new code has been sent to your email.');
      setCooldown(RESEND_COOLDOWN_SECONDS);
    } catch (err) {
      const errorMessage = err.userMessage || getErrorMessage(err, 'Could not resend the code. Please try again.');
      setError(errorMessage);
    } finally {
      setResending(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        background: BRAND.main,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <Box sx={{ position: 'absolute', top: { xs: 16, sm: 24 }, left: { xs: 16, sm: 24 }, zIndex: 10 }}>
        <Button
          onClick={() => navigate('/')}
          startIcon={<ArrowBackIcon />}
          sx={{
            color: 'white',
            fontWeight: 600,
            px: { xs: 2, sm: 3 },
            py: { xs: 1, sm: 1.2 },
            borderRadius: '12px',
            textTransform: 'none',
            backgroundColor: 'rgba(255, 255, 255, 0.15)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.25)',
              transform: 'translateX(-3px)',
            },
          }}
        >
          Back to Home
        </Button>
      </Box>

      <Container
        maxWidth="sm"
        sx={{ flex: 1, display: 'flex', alignItems: 'center', pt: { xs: 10, sm: 8 }, pb: { xs: 4, sm: 8 }, px: { xs: 2, sm: 3 }, position: 'relative', zIndex: 1 }}
      >
        <div style={{ width: '100%' }}>
          <Card
            sx={{
              borderRadius: '24px',
              overflow: 'hidden',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
              background: 'rgba(255, 255, 255, 0.98)',
              backdropFilter: 'blur(20px)',
              border: '1px solid rgba(255, 255, 255, 0.3)',
              mt: { xs: 6, sm: 0 },
            }}
          >
            <Box sx={{ p: { xs: 4, sm: 6 } }}>
              <Box sx={{ textAlign: 'center', mb: 4 }}>
                <Avatar
                  sx={{
                    width: 80,
                    height: 80,
                    mx: 'auto',
                    mb: 2,
                    background: SUCCESS.main,
                    boxShadow: '0 8px 24px rgba(16, 185, 129, 0.4)',
                  }}
                >
                  <MarkEmailReadIcon sx={{ fontSize: 40 }} />
                </Avatar>

                <Typography
                  variant="h4"
                  component="h1"
                  sx={{ fontWeight: 800, color: NEUTRAL.text, mb: 1, fontSize: { xs: '1.75rem', sm: '2rem' } }}
                >
                  Verify Your Email
                </Typography>
                <Typography variant="body1" sx={{ color: NEUTRAL.textSecondary, fontSize: '1rem' }}>
                  We sent a 6-digit code to{' '}
                  <Box component="span" sx={{ fontWeight: 700, color: '#4338ca' }}>
                    {email}
                  </Box>
                  . Enter it below to activate your account.
                </Typography>
              </Box>

              {error && (
                <Alert severity="error" sx={{ mb: 3, borderRadius: '12px' }}>
                  {error}
                </Alert>
              )}
              {info && (
                <Alert severity="success" sx={{ mb: 3, borderRadius: '12px' }}>
                  {info}
                </Alert>
              )}

              <Box component="form" onSubmit={handleSubmit}>
                <CodeTextField
                  fullWidth
                  autoFocus
                  value={code}
                  onChange={handleCodeChange}
                  placeholder="------"
                  inputProps={{
                    inputMode: 'numeric',
                    maxLength: 6,
                    'aria-label': 'Verification code',
                  }}
                />

                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  disabled={loading || code.length !== 6}
                  sx={{
                    mt: 3,
                    py: 1.5,
                    borderRadius: '12px',
                    textTransform: 'none',
                    fontSize: '1rem',
                    fontWeight: 700,
                    background: BRAND.main,
                    boxShadow: '0 8px 24px rgba(102, 126, 234, 0.4)',
                    '&:hover': {
                      background: BRAND.dark,
                    },
                  }}
                >
                  {loading ? <CircularProgress size={24} sx={{ color: 'white' }} /> : 'Verify Email'}
                </Button>
              </Box>

              <Box sx={{ textAlign: 'center', mt: 3 }}>
                <Typography variant="body2" sx={{ color: NEUTRAL.textSecondary }}>
                  Didn&apos;t get the code?{' '}
                  <Link
                    component="button"
                    type="button"
                    onClick={handleResend}
                    disabled={cooldown > 0 || resending}
                    sx={{
                      fontWeight: 700,
                      color: cooldown > 0 ? '#9ca3af' : BRAND.main,
                      textDecoration: 'none',
                      cursor: cooldown > 0 ? 'default' : 'pointer',
                    }}
                  >
                    {resending
                      ? 'Sending...'
                      : cooldown > 0
                      ? `Resend in ${cooldown}s`
                      : 'Resend code'}
                  </Link>
                </Typography>
                <Typography variant="body2" sx={{ color: NEUTRAL.textSecondary, mt: 1 }}>
                  <Link
                    component="button"
                    type="button"
                    onClick={handleBackToLogin}
                    sx={{ fontWeight: 700, color: BRAND.main, textDecoration: 'none', cursor: 'pointer' }}
                  >
                    Back to login
                  </Link>
                </Typography>
              </Box>
            </Box>
          </Card>
        </div>
      </Container>
    </Box>
  );
}
